(() => {
  const rootSelector = '.shopify-app-block[id*="frengee_app_product_specifications"]';

  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const firstSentences = (value, count = 2) => {
    const matches = clean(value).match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
    return clean(matches.slice(0, count).join(' '));
  };

  const organizeDescription = (root) => {
    const content = root.querySelector('[data-section-key="description"] .rich-text-content');
    if (!content || content.dataset.sierraDescriptionOrganized === 'true') return;

    const paragraph = [...content.children].find((element) => element.tagName === 'P');
    const text = clean(paragraph?.textContent);
    if (!paragraph || text.length < 700) {
      content.dataset.sierraDescriptionOrganized = 'true';
      return;
    }

    const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map(clean).filter(Boolean) || [text];
    const groups = [];
    let current = '';

    sentences.forEach((sentence) => {
      const candidate = clean(`${current} ${sentence}`);
      if (current && candidate.length > 300) {
        groups.push(current);
        current = sentence;
      } else {
        current = candidate;
      }
    });
    if (current) groups.push(current);

    const prose = document.createElement('div');
    prose.className = 'sierra-description-prose';
    groups.forEach((group, index) => {
      const section = document.createElement('p');
      section.className = index === 0
        ? 'sierra-description-paragraph sierra-description-lead'
        : 'sierra-description-paragraph';
      section.textContent = group;
      prose.appendChild(section);
    });

    paragraph.replaceWith(prose);
    content.classList.add('sierra-description--long');
    content.dataset.sierraDescriptionOrganized = 'true';
  };

  const rowMap = (root) => {
    const rows = new Map();
    root.querySelectorAll('.frengee-specs-table tr[data-spec-key]').forEach((row) => {
      const key = clean(row.dataset.specKey).toLowerCase();
      const value = clean(row.querySelector('[data-spec-value]')?.textContent || row.cells?.[1]?.textContent);
      if (key && value) rows.set(key, value);
    });
    return rows;
  };

  const applySpecificationDensity = (root) => {
    const table = root.querySelector('.frengee-specs-table');
    if (!table) return;
    const rows = [...table.querySelectorAll('tr[data-spec-key]')];
    if (!rows.length) return;

    const lengths = rows.map((row) => clean(row.textContent).length);
    const averageLength = lengths.reduce((total, length) => total + length, 0) / lengths.length;
    const maximumLength = Math.max(...lengths);
    const hasGeometry = root.querySelector('[data-section-key="geometry"] table') != null;
    let density = 'spacious';

    if (!hasGeometry && rows.length >= 14 && averageLength <= 70 && maximumLength <= 160) {
      density = 'compact';
    } else if (!hasGeometry && rows.length >= 10) {
      density = 'balanced';
    }

    table.classList.remove('sierra-specs-density--compact', 'sierra-specs-density--balanced', 'sierra-specs-density--spacious');
    table.classList.add(`sierra-specs-density--${density}`);
    table.dataset.sierraSpecCount = String(rows.length);
  };

  const listValue = (items, label) => {
    const expression = new RegExp(`^${label}\\s*[-:]\\s*(.+)$`, 'i');
    for (const item of items) {
      const match = clean(item.textContent).match(expression);
      if (match) return clean(match[1]);
    }
    return '';
  };

  const metric = (key, value, label) => {
    const item = document.createElement('div');
    item.className = 'sierra-specs-metric';
    item.dataset.sierraMetric = key;

    const strong = document.createElement('strong');
    strong.textContent = value;

    const span = document.createElement('span');
    span.textContent = label;

    item.append(strong, span);
    return item;
  };

  const clearGeometryHighlight = (table) => {
    table.querySelectorAll('.sierra-geometry-axis, .sierra-geometry-active').forEach((cell) => {
      cell.classList.remove('sierra-geometry-axis', 'sierra-geometry-active');
    });
  };

  const highlightGeometryCell = (table, cell) => {
    const row = cell.closest('tr');
    if (!row || !table.contains(row)) return;

    clearGeometryHighlight(table);
    [...row.cells].forEach((rowCell) => rowCell.classList.add('sierra-geometry-axis'));
    [...table.rows].forEach((tableRow) => {
      tableRow.cells[cell.cellIndex]?.classList.add('sierra-geometry-axis');
    });
    cell.classList.add('sierra-geometry-active');
  };

  const bindGeometryHighlight = (table) => {
    if (table.dataset.sierraGeometryHighlightBound === 'true') return;
    table.dataset.sierraGeometryHighlightBound = 'true';

    table.addEventListener('pointerover', (event) => {
      const cell = event.target.closest('th, td');
      if (cell && table.contains(cell)) highlightGeometryCell(table, cell);
    });
    table.addEventListener('pointerleave', () => clearGeometryHighlight(table));
    table.addEventListener('click', (event) => {
      const cell = event.target.closest('th, td');
      if (cell && table.contains(cell)) highlightGeometryCell(table, cell);
    });
  };

  const wrapGeometryTables = (root) => {
    root.querySelectorAll('[data-section-key="geometry"] table').forEach((table) => {
      bindGeometryHighlight(table);
      if (table.closest('.frengee-geometry-table-container')) return;
      const container = document.createElement('div');
      container.className = 'frengee-geometry-table-container';
      table.parentNode.insertBefore(container, table);
      container.appendChild(table);
    });
  };

  const sourceMetrics = (root) => {
    const description = root.querySelector('[data-section-key="description"] .rich-text-content');
    const specs = rowMap(root);
    const sourceItems = [...(description?.querySelectorAll('li') || [])];
    const allSourceText = clean(description?.textContent);
    const measurementTable = [...root.querySelectorAll('[data-section-key="geometry"] table')]
      .find((table) => clean(table.querySelector('thead th')?.textContent).toLowerCase() === 'size');

    const weightMatch = allSourceText.match(/\b(\d+(?:\.\d+)?)\s*(?:grams?|g)\b/i);
    const carbonMatch = allSourceText.match(/\bFACT\s*(\d+r)\b/i)
      || clean(specs.get('frame')).match(/\bFACT\s*(\d+r)\b/i);
    const sizeCount = Math.max(0, (measurementTable?.querySelectorAll('thead th').length || 1) - 1);
    const useValue = listValue(sourceItems, 'Use') || specs.get('use') || 'Performance';

    return {
      weight: {
        value: weightMatch ? `${weightMatch[1]}g` : '—',
        label: 'Frame weight'
      },
      carbon: {
        value: carbonMatch?.[1] || specs.get('material') || '—',
        label: carbonMatch ? 'FACT carbon' : 'Frame material'
      },
      sizes: {
        value: sizeCount ? String(sizeCount) : clean(specs.get('size')) || '—',
        label: sizeCount ? 'Frame sizes' : 'Selected size'
      },
      use: {
        value: useValue.replace(/\s+racing\b/i, ''),
        label: /race|racing/i.test(useValue) ? 'Race use' : 'Intended use'
      },
      carbonMark: carbonMatch?.[1]?.toUpperCase() || 'DNA'
    };
  };

  const refreshMetrics = (root) => {
    wrapGeometryTables(root);
    applySpecificationDensity(root);
    const values = sourceMetrics(root);

    Object.entries(values).forEach(([key, content]) => {
      if (key === 'carbonMark') return;
      const item = root.querySelector(`[data-sierra-metric="${key}"]`);
      const strong = item?.querySelector('strong');
      const span = item?.querySelector('span');
      if (strong && strong.textContent !== content.value) strong.textContent = content.value;
      if (span && span.textContent !== content.label) span.textContent = content.label;
      if (item) {
        const length = clean(content.value).length;
        item.classList.toggle('sierra-specs-metric--long', length >= 10);
        item.classList.toggle('sierra-specs-metric--extra-long', length >= 15);
      }
    });

    const visual = root.querySelector('.sierra-specs-visual');
    if (visual && visual.dataset.carbonMark !== values.carbonMark) {
      visual.dataset.carbonMark = values.carbonMark;
    }
  };

  const observeRoot = (root) => {
    if (root.dataset.sierraSpecsObserver === 'true') return;
    root.dataset.sierraSpecsObserver = 'true';

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        organizeDescription(root);
        enhance(root);
        refreshMetrics(root);
      });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  const enhance = (root) => {
    const productSpecs = root.querySelector('.frengee-product-specifications[data-ps-initialized="true"]');
    const tabs = root.querySelector('.frengee-product-tabs');
    const description = root.querySelector('[data-section-key="description"] .rich-text-content');
    const specsTable = root.querySelector('.frengee-specs-table');
    const geometryTable = root.querySelector('[data-section-key="geometry"] table');

    if (!productSpecs || !tabs || !description || !specsTable) return false;
    if (!geometryTable) {
      applySpecificationDensity(root);
      return true;
    }
    if (productSpecs.dataset.sierraRedesign === 'true') {
      refreshMetrics(root);
      return true;
    }

    const descriptionText = clean(description.querySelector('p')?.textContent);
    const productTitle = clean(document.querySelector('h1')?.textContent || document.title.split('–')[0]);
    const metricsData = sourceMetrics(root);

    wrapGeometryTables(root);

    const story = document.createElement('section');
    story.className = 'sierra-specs-story';
    story.setAttribute('aria-label', 'Product performance overview');

    const copy = document.createElement('div');
    copy.className = 'sierra-specs-story-copy';

    const kicker = document.createElement('p');
    kicker.className = 'sierra-specs-kicker';
    kicker.textContent = `${productTitle.replace(/\s+-\s+.*$/, '')} · Performance DNA`;

    const heading = document.createElement('h2');
    heading.textContent = /crux/i.test(productTitle)
      ? 'Built for the fastest line.'
      : 'Performance, down to every detail.';

    const intro = document.createElement('p');
    intro.className = 'sierra-specs-intro';
    intro.textContent = firstSentences(descriptionText, 2) || descriptionText;

    copy.append(kicker, heading, intro);

    const visual = document.createElement('div');
    visual.className = 'sierra-specs-visual';
    visual.dataset.carbonMark = metricsData.carbonMark;
    visual.setAttribute('aria-hidden', 'true');

    const diagonal = document.createElement('span');
    diagonal.className = 'sierra-specs-diagonal';

    const metrics = document.createElement('div');
    metrics.className = 'sierra-specs-metrics';
    metrics.append(
      metric('weight', metricsData.weight.value, metricsData.weight.label),
      metric('carbon', metricsData.carbon.value, metricsData.carbon.label),
      metric('sizes', metricsData.sizes.value, metricsData.sizes.label),
      metric('use', metricsData.use.value, metricsData.use.label)
    );

    visual.append(diagonal, metrics);
    story.append(copy, visual);
    productSpecs.insertBefore(story, tabs);

    const firstTab = root.querySelector('.frengee-tab-item[data-section-source="api"]');
    if (firstTab && clean(firstTab.textContent).toLowerCase() === 'description') {
      firstTab.textContent = 'Overview';
      firstTab.setAttribute('aria-label', 'Overview');
    }

    productSpecs.dataset.sierraRedesign = 'true';
    refreshMetrics(root);
    return true;
  };

  const initialize = () => {
    const roots = [...document.querySelectorAll(rootSelector)];
    let pending = false;

    roots.forEach((root) => {
      observeRoot(root);
      organizeDescription(root);
      applySpecificationDensity(root);
      if (!enhance(root)) pending = true;
    });

    return { found: roots.length > 0, pending };
  };

  const initial = initialize();
  if (!initial.found || initial.pending) {
    let attempts = 0;
    const observer = new MutationObserver(() => {
      const result = initialize();
      if (result.found && !result.pending) {
        observer.disconnect();
        window.clearInterval(retryTimer);
      }
    });

    const retryTimer = window.setInterval(() => {
      attempts += 1;
      const result = initialize();
      if ((result.found && !result.pending) || attempts >= 40) {
        observer.disconnect();
        window.clearInterval(retryTimer);
      }
    }, 500);

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
